import {
  areFiltersSelected,
  LoadingIndicator,
  NoResultsIndicatorAnimation,
  useDeviceFilterValue
} from "elements";
import { ProtectedRoute } from "TsRoleGuards";
import {
  getColumns,
  getSelectedWithoutHeader,
  HEADER_ID,
  itemCheckedWithSet,
  TableBody,
  TableBulkItemHeader,
  TableHeaderItem,
  TableSearchEdit,
  toggleAll,
  toggleItemWithSet
} from "collections/Table";
import { ConnectionStatusHeader } from "routes/Manage/Inventory/List/TableView";
import { generateTableHeaders } from "utilities/tableUtils";
import React from "react";
import { DeviceUserRow } from "./DeviceUserRow";
import { LocalKeys, useTranslation } from "utilities/i18n";
import { TABLE_COLUMN_STATE, TABLES } from "store/userMeta";
import { useSelector } from "react-redux";
import { State } from "store";
import AllRoles from "roles";
import { DeviceUserInfo } from "../utils/deviceUserUtils";

export type DeviceUsersTableRoute = {
  nestedRoute: string;
  requirement: AllRoles;
  refresh: () => void;
  Modal: React.FC<{
    deviceUsers: Array<{ name: string; id: string }>;
  }>;
};

export type DeviceUsersTableProps = {
  actions: Array<
    React.FC<{
      disabled: boolean;
    }>
  >;
  routes: Array<DeviceUsersTableRoute>;
  loading: boolean;
  userCanEdit: boolean;
  setUsersSearchTerm?: (term: string | undefined) => void;
  hasNextPage?: boolean;
  endCursor?: string;
  deviceUserList: Array<DeviceUserInfo>;
  selectedItems: string[];
  setSelectedItems: (items: string[]) => void;
  hiddenItems?: string[];
  hasDeviceUsers: boolean;
  noUsersMessage?: LocalKeys;
  updateCursor?: (cursor: string) => void;
};

export const DeviceUsersTable: React.FC<DeviceUsersTableProps> = ({
  actions,
  routes,
  loading,
  userCanEdit,
  setUsersSearchTerm,
  hasNextPage,
  endCursor,
  deviceUserList,
  selectedItems,
  setSelectedItems,
  hiddenItems,
  hasDeviceUsers,
  noUsersMessage,
  updateCursor
}) => {
  const [filterState, setFilter, resetFilters] = useDeviceFilterValue();

  const { t } = useTranslation();

  const toggleItem = toggleItemWithSet(
    deviceUserList.length,
    selectedItems,
    setSelectedItems
  );
  const selectedUsers = getSelectedWithoutHeader(selectedItems);
  const itemChecked = itemCheckedWithSet(selectedItems);

  const { NAME: TABLE_NAME, COLUMNS } = TABLES.DEVICE_USERS;
  const {
    [TABLE_COLUMN_STATE.SHOWN]: shownColumns,
    [TABLE_COLUMN_STATE.HIDDEN]: hiddenColumns
  } = useSelector((state: State) => {
    return state.userMeta?.tables[TABLE_NAME] ?? {};
  });

  const columnTranslations = {
    [COLUMNS.NAME]: t("DEVICE_USER_NAME"),
    [COLUMNS.PRIMARY_DEVICE]: t("PRIMARY_DEVICE"),
    [COLUMNS.APP_VERSION]: t("CLIENT_APP_VERSION"),
    [COLUMNS.DEVICE_COUNT]: t("DEVICE_COUNT"),
    [COLUMNS.LAST_DETECTED]: t("LAST_DETECTED"),
    [COLUMNS.CLIENT]: t("CLIENT")
  };

  const columnOrder = [
    COLUMNS.NAME,
    COLUMNS.PRIMARY_DEVICE,
    COLUMNS.APP_VERSION,
    COLUMNS.DEVICE_COUNT,
    COLUMNS.LAST_DETECTED,
    COLUMNS.CLIENT
  ];

  const getTableHeaders = (column: string, title: string) => {
    return <TableHeaderItem fieldName={column} title={title} key={column} />;
  };
  const shown = getColumns(shownColumns, columnTranslations);
  shown.sort((a, b) => columnOrder.indexOf(a.key) - columnOrder.indexOf(b.key));
  const hidden = getColumns(hiddenColumns, columnTranslations);

  // there are two additional columns besides those user choose to display
  // (checkbox to select rows and user online status)
  const columnCount = shown.length + 2;
  const usersSelected = selectedUsers.length > 0;

  // filtering logic for device users.
  // We can filter based on filter state (from user input in table headers),
  // or from a list of hidden device users.
  const filteredUsers = deviceUserList.filter(deviceUser => {
    const connected = deviceUser.lensDesktopInfo?.connected;
    const filteredFromState =
      filterState &&
      ((filterState.status === "online" && !connected) ||
        (filterState.status === "offline" && connected));
    // early return to avoid unnecessary traversal of 'hiddenItems'
    if (filteredFromState) {
      return false;
    }
    // if not filtered from state, check if our user is hidden
    return !hiddenItems?.includes(deviceUser.user_id);
  });

  return (
    <>
      {!loading && !hasDeviceUsers ? (
        <NoResultsIndicatorAnimation
          className="inline pt-4"
          title={t("NO_DEVICE_USERS")}
        >
          {noUsersMessage && (
            <span className="font-sm">{t(noUsersMessage)}</span>
          )}
        </NoResultsIndicatorAnimation>
      ) : (
        <>
          <div className="grouping mb-15 table-actions">
            {actions.map((Action, index) => (
              <Action
                key={`user-table-action-${index}`}
                disabled={!usersSelected}
              />
            ))}
            <TableSearchEdit
              search={
                setUsersSearchTerm
                  ? {
                      onSearch: setUsersSearchTerm
                    }
                  : undefined
              }
              edit={{
                tableName: TABLE_NAME,
                shownColumns: shown,
                hiddenColumns: hidden
              }}
            />
          </div>
          <div className="table-container">
            <table className="table custom-table table-filterable">
              <TableBulkItemHeader
                showChecks={userCanEdit}
                checked={itemChecked(HEADER_ID)}
                onCheck={() =>
                  toggleAll(
                    filteredUsers.map(user => user.user_id),
                    selectedItems,
                    setSelectedItems
                  )
                }
                onClearFilters={resetFilters}
                showClearFilters={areFiltersSelected(filterState)}
              >
                <ConnectionStatusHeader
                  fieldName="status"
                  filterState={filterState}
                  setFilter={setFilter}
                />
                {generateTableHeaders(shown, getTableHeaders)}
              </TableBulkItemHeader>
              <TableBody
                loading={false}
                cols={columnCount}
                accommodateScrollbar
              >
                {filteredUsers.map((user, i) => {
                  return (
                    <DeviceUserRow
                      {...user}
                      key={user.user_id}
                      hasNextPage={hasNextPage ?? false}
                      index={i}
                      userListLength={filteredUsers.length}
                      endCursor={endCursor}
                      onCheck={id => toggleItem(id)}
                      checked={itemChecked(user.user_id)}
                      showChecks={userCanEdit}
                      shownColumns={shown.map(column => column.key)}
                      updateCursor={updateCursor}
                      loading={loading}
                    />
                  );
                })}
              </TableBody>
            </table>
            {loading ? (
              <LoadingIndicator
                key="loading"
                className={deviceUserList.length ? "inline text-lg" : ""}
              />
            ) : null}
          </div>
        </>
      )}
      {routes.map(({ Modal, nestedRoute, requirement }) => (
        <ProtectedRoute
          key={`table-route-${nestedRoute?.replace(/[\W\-_]+/g, "")}`}
          nest
          path={nestedRoute}
          requirement={requirement}
        >
          <Modal deviceUsers={selectedUsers.map(id => ({ id, name: "" }))} />
        </ProtectedRoute>
      ))}
    </>
  );
};
